import React, { useState, useRef, Fragment, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Patient, Encounter } from '../../types';
import { Search, FileText, Printer, Trash2, Download, Eye, ZoomIn, ZoomOut, X } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
/**
 * Capture a DOM element and export it as a properly paginated A4 PDF.
 *
 * A4 layout: 210mm × 297mm, margins 20mm top/bottom, 18mm left/right.
 * Content slices are taken from the source canvas one page-height at a time
 * so rows never get clipped mid-content across page breaks.
 */
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  // A4 geometry (millimetres)
  const PAGE_W_MM = 210;
  const PAGE_H_MM = 297;
  const MARGIN_TOP_MM = 20;
  const MARGIN_BOTTOM_MM = 20;
  const MARGIN_LEFT_MM = 18;
  const MARGIN_RIGHT_MM = 18;
  const CONTENT_W_MM = PAGE_W_MM - MARGIN_LEFT_MM - MARGIN_RIGHT_MM;
  const CONTENT_H_MM = PAGE_H_MM - MARGIN_TOP_MM - MARGIN_BOTTOM_MM;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  // How many source pixels equal one printed content page (in canvas pixels)?
  const sourceW = sourceCanvas.width;
  const sourceH = sourceCanvas.height;
  const pxPerMm = sourceW / CONTENT_W_MM;
  const pageSlicePx = Math.floor(CONTENT_H_MM * pxPerMm);
  // Slice the source canvas page-by-page; render each slice into its own
  // temporary canvas, paint that into the PDF. This avoids the "shift the
  // whole image up" trick that clips rows mid-cell.
  let cursorPx = 0;
  let pageIndex = 0;
  while (cursorPx < sourceH) {
    const remainingPx = sourceH - cursorPx;
    const sliceHpx = Math.min(pageSlicePx, remainingPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = sourceW;
    sliceCanvas.height = sliceHpx;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) break;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sourceW, sliceHpx);
    ctx.drawImage(sourceCanvas, 0, cursorPx, sourceW, sliceHpx, 0, 0, sourceW, sliceHpx);
    const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.95);
    const sliceHmm = sliceHpx / pxPerMm;
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(sliceImg, 'JPEG', MARGIN_LEFT_MM, MARGIN_TOP_MM, CONTENT_W_MM, sliceHmm);
    cursorPx += sliceHpx;
    pageIndex += 1;
  }
  pdf.save(filename);
}
import { fbcStructure, urinalysisTests, testosteroneTests, hormonalProfileTests, amhStructure, hcgStructure, isHCGTest, isHCGAbnormal, isFBCTest, isUrinalysisTest, isTestosteroneTest, isHormonalProfileTest, isAMHTest } from '../../store/labStructures';
type ViewingTest = {
  kind: 'lab';
  encounterId: string;
  labId: string;
} | {
  kind: 'rad';
  encounterId: string;
  radId: string;
} | null;
export const PatientFiles: React.FC = () => {
  const {
    patients,
    encounters,
    deletePatient,
    currentUser
  } = useData();
  const navigate = useNavigate();
  // Amendment modal — Clinicians can reopen a completed encounter, provide a
  // required reason, and the Clinician Dashboard picks up that context on
  // arrival to log audit events on every save.
  const [amendModal, setAmendModal] = useState<{
    isOpen: boolean;
    encounterId: string;
    reason: string;
  }>({
    isOpen: false,
    encounterId: '',
    reason: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [viewingTest, setViewingTest] = useState<ViewingTest>(null);
  const [imageZoom, setImageZoom] = useState<number>(100); // percentage 10-200
  const [isGeneratingTestPdf, setIsGeneratingTestPdf] = useState(false);
  const printableRef = useRef<HTMLDivElement>(null);
  const testModalRef = useRef<HTMLDivElement>(null);
  const filteredPatients = patients.filter(p => p.firstName.toLowerCase().includes(searchQuery.toLowerCase()) || p.lastName.toLowerCase().includes(searchQuery.toLowerCase()) || p.bmcFileNumber.toLowerCase().includes(searchQuery.toLowerCase()));
  const getPatientEncounters = (patientId: string) => {
    return encounters.filter(e => e.patientId === patientId).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  };
  const handleDeletePatient = (id: string) => {
    if (window.confirm('WARNING: This will permanently delete the patient and ALL associated medical records. Are you sure?')) {
      deletePatient(id);
      setSelectedPatient(null);
    }
  };
  const handlePrint = () => {
    window.print();
  };
  const handleDownloadPdf = async () => {
    if (!printableRef.current || !selectedPatient) return;
    setIsGeneratingPdf(true);
    try {
      const filename = `BMED_PatientFile_${selectedPatient.bmcFileNumber}_${selectedPatient.firstName}_${selectedPatient.lastName}.pdf`;
      await captureToA4Pdf(printableRef.current, filename);
    } catch (err) {
      console.error('PDF generation failed', err);
      alert('Failed to generate PDF. Please try again or use the Print option.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  // ─── Test File modal helpers ───
  const closeTestModal = () => {
    setViewingTest(null);
    setImageZoom(100);
  };
  const handlePrintTestModal = () => {
    // Use a print-only class on body so only the modal prints
    document.body.classList.add('printing-test-modal');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-test-modal');
    }, 500);
  };
  const handleDownloadTestPdf = async () => {
    if (!testModalRef.current) return;
    setIsGeneratingTestPdf(true);
    try {
      const filename = `BMED_TestResult_${selectedPatient?.bmcFileNumber || 'patient'}_${Date.now()}.pdf`;
      await captureToA4Pdf(testModalRef.current, filename);
    } catch (err) {
      console.error('Test PDF generation failed', err);
      alert('Failed to generate PDF.');
    } finally {
      setIsGeneratingTestPdf(false);
    }
  };
  // ─── PATIENT FILE VIEW ───
  if (selectedPatient) {
    const patientEncounters = getPatientEncounters(selectedPatient.id);
    return <div className="space-y-6" data-id="element-1905" data-mp="xtze9y9q8m9l">
        {/* Action Bar */}
        <div className="flex justify-between items-center print:hidden" data-id="element-1906" data-mp="1bs62snssd9wl">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1907" data-mp="1bq33te9no6fq">Patient File</h1>
          <div className="flex space-x-2" data-id="element-1908" data-mp="17soyhxp3iit0">
            <Button variant="outline" onClick={() => setSelectedPatient(null)} data-id="element-1909" data-mp="1qfec1j2jb55x">
              Back to Directory
            </Button>
            <Button variant="outline" onClick={handlePrint} data-id="element-1910" data-mp="1vcxz98h7msa0">
              <Printer className="w-4 h-4 mr-2" data-id="element-1911" data-mp="1heq67bnaob44" /> Print
            </Button>
            <Button variant="primary" onClick={handleDownloadPdf} disabled={isGeneratingPdf} data-id="element-1912" data-mp="1hgsv1scpny79">
              <Download className="w-4 h-4 mr-2" data-id="element-1913" data-mp="17nlt6hh6j0bx" />
              {isGeneratingPdf ? 'Generating PDF...' : 'Download PDF'}
            </Button>
            {currentUser?.role === 'Administrator' && <Button variant="danger" onClick={() => handleDeletePatient(selectedPatient.id)} data-id="element-1914" data-mp="yp02fz1k0f02">
                <Trash2 className="w-4 h-4 mr-2" data-id="element-1915" data-mp="12nhcaqcd03l6" /> Delete File
              </Button>}
          </div>
        </div>

        {/* Encounter Status Summary (screen only) */}
        <div className="print:hidden bg-white rounded-lg shadow-sm p-4 border border-gray-200" data-id="element-1916" data-mp="to3yxml46uv6">
          <h3 className="text-sm font-semibold text-gray-700 mb-2" data-id="element-1917" data-mp="196dfeokz2vsr">
            Encounter History ({patientEncounters.length})
          </h3>
          <div className="flex flex-wrap gap-2" data-id="element-1918" data-mp="105ypfbx2csix">
            {patientEncounters.map((enc, idx) => <span key={enc.id} className={`px-3 py-1 rounded-full text-xs font-medium ${enc.stage === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-1919" data-mp="v7dy61zshj6m" data-mpi={idx}>
                Visit {patientEncounters.length - idx}:{' '}
                {new Date(enc.startedAt).toLocaleDateString()} •{' '}
                {enc.stage === 'COMPLETED' ? '✓ Completed' : enc.stage}
              </span>)}
          </div>
        </div>

        {/* All Encounters – Always Expanded — wrapped in ref for PDF capture */}
        <div ref={printableRef} data-id="element-1920" data-mp="7je1klfsqyk4">
          {patientEncounters.length > 0 ? patientEncounters.map((enc, index) => <div key={enc.id} className={`bg-white border-2 border-gray-300 rounded-lg p-6 mb-6 ${index > 0 ? 'print:break-before-page pdf-page-break' : ''}`} data-id="element-1921" data-mp="2e6lcpo0crjrm" data-mpi={index}>
                {/* Clinician-only Amend action for completed visits */}
                {currentUser?.role === 'Clinician' && enc.stage === 'COMPLETED' && <div className="print:hidden flex justify-end mb-3" data-id="element-1922" data-mp="1vfu5ot3u85ge">
                      <Button variant="outline" className="text-amber-700 border-amber-400 hover:bg-amber-50" onClick={() => setAmendModal({
              isOpen: true,
              encounterId: enc.id,
              reason: ''
            })} data-id="element-1923" data-mp="1vdhia0fb92vy">
                        <FileText className="w-4 h-4 mr-2" data-id="element-1924" data-mp="1gp3ffphw9efe" />
                        Amend File
                      </Button>
                    </div>}
                {/* ═══ FORM HEADER ═══ */}
                <div className="flex justify-between items-start mb-6" data-id="element-1925" data-mp="2auzhhkdi90sw">
                  <div className="flex items-center" data-id="element-1926" data-mp="2exw73v1gpx6c">
                    <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-20 w-20 object-contain mr-4" data-id="element-1927" data-mp="2ewbc64b8kc7y" />
                    <div data-id="element-1928" data-mp="1r9cvp6n494a1">
                      <h1 className="text-2xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-1929" data-mp="1r6h21uiax1re">
                        BMED MEDICAL CENTRE
                      </h1>
                      <p className="text-sm italic text-gray-600" data-id="element-1930" data-mp="20z4uye65ulku">
                        Caring Healing Leading
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-1931" data-mp="18h0n425vd6rg">
                    <p className="font-bold text-lg uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-1932" data-mp="18fpk44k9031e">
                      Confidential
                    </p>
                    <p className="text-sm" data-id="element-1933" data-mp="vzzojmo3s01w">
                      <span className="font-semibold" data-id="element-1934" data-mp="r03i7grxnwj6">Date:</span>{' '}
                      {new Date(enc.startedAt).toLocaleDateString()}
                    </p>
                    <p className="text-sm" data-id="element-1935" data-mp="myi5bvfefi8z">
                      <span className="font-semibold" data-id="element-1936" data-mp="i0705y01tvx3">File No:</span>{' '}
                      {selectedPatient.bmcFileNumber}
                    </p>
                  </div>
                </div>

                {/* ═══ PERSONAL DETAILS ═══ */}
                <div className="border-2 border-black p-4 mb-6 text-sm space-y-3" data-id="element-1937" data-mp="8a59qzk841f0">
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-1938" data-mp="3c3pibb29w1y">
                    <div className="col-span-1 font-semibold" data-id="element-1939" data-mp="3vdjsz77ipa3g">1.</div>
                    <div className="col-span-6" data-id="element-1940" data-mp="3qfqku8mnm60j">
                      <span className="font-semibold" data-id="element-1941" data-mp="3qg0cs1r9en8v">Full Names:</span>{' '}
                      {selectedPatient.firstName} {selectedPatient.lastName}
                    </div>
                    <div className="col-span-3" data-id="element-1942" data-mp="3kuj642gr8ip6">
                      <span className="font-semibold" data-id="element-1943" data-mp="36tos94axtpj4">Sex:</span>{' '}
                      {selectedPatient.sex}
                    </div>
                    <div className="col-span-2" data-id="element-1944" data-mp="3btuokxs5k07j">
                      <span className="font-semibold" data-id="element-1945" data-mp="3bumlqd1ee5zx">Age:</span>{' '}
                      {selectedPatient.age}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-1946" data-mp="8aro569mlkbw">
                    <div className="col-span-1 font-semibold" data-id="element-1947" data-mp="3prjc6ddltlga">2.</div>
                    <div className="col-span-11" data-id="element-1948" data-mp="3ure1wdln84zr">
                      <span className="font-semibold" data-id="element-1949" data-mp="3uq2tu1l60b2u">Marital Status:</span>{' '}
                      {selectedPatient.maritalStatus}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-1950" data-mp="r3cdweu139bh">
                    <div className="col-span-1 font-semibold" data-id="element-1951" data-mp="2brxzk4nf5eos">3.</div>
                    <div className="col-span-7" data-id="element-1952" data-mp="26u4rf62k2alv">
                      <span className="font-semibold" data-id="element-1953" data-mp="26td7isibm1bf">Home Address:</span>{' '}
                      {selectedPatient.address}
                    </div>
                    <div className="col-span-4" data-id="element-1954" data-mp="2gj686ygxcw0w">
                      <span className="font-semibold" data-id="element-1955" data-mp="2gkhnnvjqfs7u">Contact:</span>{' '}
                      {selectedPatient.contact}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-1956" data-mp="1nw2brhvwdq0q">
                    <div className="col-span-1 font-semibold" data-id="element-1957" data-mp="21v1y2ivxtstq">4.</div>
                    <div className="col-span-11" data-id="element-1958" data-mp="1wvp3fbytrhgl">
                      <span className="font-semibold" data-id="element-1959" data-mp="1wuxj4xaoh65m">Occupation:</span>{' '}
                      {selectedPatient.occupation || '_________________________'}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-1960" data-mp="38cyn9zlv8bif">
                    <div className="col-span-1 font-semibold" data-id="element-1961" data-mp="7hr3z1djhorv">5.</div>
                    <div className="col-span-5" data-id="element-1962" data-mp="cfitjoqpoysw">
                      <span className="font-semibold" data-id="element-1963" data-mp="hdu3s0kx5bbn">Next of Kin:</span>{' '}
                      {selectedPatient.nextOfKin}
                    </div>
                    <div className="col-span-3" data-id="element-1964" data-mp="mdouo44p20wp">
                      <span className="font-semibold" data-id="element-1965" data-mp="qeim37hchxuc">Contact:</span>{' '}
                      {selectedPatient.nokContact}
                    </div>
                    <div className="col-span-3" data-id="element-1966" data-mp="vcajgh0rec5x">
                      <span className="font-semibold" data-id="element-1967" data-mp="104llpqiz2a1n">Relationship:</span>{' '}
                      {selectedPatient.nokRelationship}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-1968" data-mp="15599827zs8r4">
                    <div className="col-span-1 font-semibold" data-id="element-1969" data-mp="1a0ex36kyfxhu">6.</div>
                    <div className="col-span-11" data-id="element-1970" data-mp="1e4wex07r80pz">
                      <span className="font-semibold" data-id="element-1971" data-mp="2ym5gs6ln481i">Allergies:</span>{' '}
                      <span className="text-red-600 font-medium" data-id="element-1972" data-mp="2yk1718128etb">
                        {selectedPatient.allergies}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2" data-id="element-1973" data-mp="2pnjtxz94y6ux">
                    <div className="col-span-1 font-semibold" data-id="element-1974" data-mp="2kne7qylmxck8">7.</div>
                    <div className="col-span-11" data-id="element-1975" data-mp="3i9v3hzepzi3v">
                      <span className="font-semibold" data-id="element-1976" data-mp="3i9lf2g9bpj0a">HIV Status:</span>{' '}
                      <span className="ml-4" data-id="element-1977" data-mp="3dc33ei3x8pol">
                        <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${selectedPatient.hivStatus === 'Positive' ? 'bg-red-100 font-bold' : ''}`} data-id="element-1978" data-mp="3dde58cdt3nd2">
                          Positive{' '}
                          {selectedPatient.hivStatus === 'Positive' ? '✓' : ''}
                        </span>
                        <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${selectedPatient.hivStatus === 'Negative' ? 'bg-green-100 font-bold' : ''}`} data-id="element-1979" data-mp="2xb4tq0f1w7">
                          Negative{' '}
                          {selectedPatient.hivStatus === 'Negative' ? '✓' : ''}
                        </span>
                        <span className={`inline-block px-2 py-0.5 border border-black ${selectedPatient.hivStatus === 'Unknown' ? 'bg-yellow-100 font-bold' : ''}`} data-id="element-1980" data-mp="1pku8axz5qc7r">
                          Unknown{' '}
                          {selectedPatient.hivStatus === 'Unknown' ? '✓' : ''}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* ═══ NURSING OBSERVATIONS ═══ */}
                <div className="mb-6" data-id="element-1981" data-mp="2w30rseh5xo7y">
                  <h3 className="font-bold uppercase bg-gray-200 p-2 border-2 border-black border-b-0 text-center" data-id="element-1982" data-mp="2vzuzyrh5g88j">
                    Nursing Observations
                  </h3>
                  <table className="w-full border-collapse border-2 border-black text-sm text-center" data-id="element-1983" data-mp="3b4zacgi2gobb">
                    <thead data-id="element-1984" data-mp="363jmkgoi83p8">
                      <tr className="bg-gray-50" data-id="element-1985" data-mp="3jwssg7d8u730">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <th key={n} className="border border-black p-1 text-xs text-gray-500" data-id="element-1986" data-mp="3f62pf0saigbh">
                            {n}
                          </th>)}
                      </tr>
                      <tr className="bg-gray-100" data-id="element-1987" data-mp="6qsq20fsbp7e">
                        <th className="border border-black p-1" data-id="element-1988" data-mp="6oesnkxmu0wo">Temp</th>
                        <th className="border border-black p-1" data-id="element-1989" data-mp="2pfhvjp7z9xb">Pulse</th>
                        <th className="border border-black p-1" data-id="element-1990" data-mp="28h45vh3wja2q">Resp</th>
                        <th className="border border-black p-1" data-id="element-1991" data-mp="24gk7693ojyeh">BP</th>
                        <th className="border border-black p-1" data-id="element-1992" data-mp="24idgjw5ogwq6">SpO2</th>
                        <th className="border border-black p-1" data-id="element-1993" data-mp="2t0fpyfop0o3r">Weight (Kg)</th>
                        <th className="border border-black p-1" data-id="element-1994" data-mp="2xziix1ref3vd">Height</th>
                        <th className="border border-black p-1" data-id="element-1995" data-mp="32racll5upppq">BMI</th>
                        <th className="border border-black p-1" data-id="element-1996" data-mp="32t4wcw5dzbs8">Pain Score</th>
                        <th className="border border-black p-1" data-id="element-1997" data-mp="36s42gk2utel9">AVPU</th>
                      </tr>
                    </thead>
                    <tbody data-id="element-1998" data-mp="2a4pkgihjx9l4">
                      <tr data-id="element-1999" data-mp="2f0xu5gczcakw">
                        <td className="border border-black p-2 font-medium" data-id="element-2000" data-mp="2f28xxg6fdic0">
                          {enc.vitals?.temp || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-2001" data-mp="2o1w1o8mmn088">
                          {enc.vitals?.pulse || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-2002" data-mp="3uhonr9mhcnxp">
                          {enc.vitals?.resp || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-2003" data-mp="dgd9ytmz4a9y">
                          {enc.vitals?.bp || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-2004" data-mp="8itxnz9u67kn">
                          {enc.vitals?.sp02 || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-2005" data-mp="3vsry81kplayj">
                          {enc.vitals?.weight || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-2006" data-mp="3lvn9btzhb1e1">
                          {enc.vitals?.height || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-2007" data-mp="3gx2jmohus03u">
                          {enc.vitals?.bmi || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-2008" data-mp="1f4xn0zpesems">
                          {enc.vitals?.painScore || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-2009" data-mp="1b1hpm15ima91">
                          {enc.vitals?.avpu || ''}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* ═══ CLINICAL NOTES ═══ */}
                <div className="space-y-5 mb-6 text-sm" data-id="element-2010" data-mp="350d4x42f38t5">
                  <div className="border-2 border-black" data-id="element-2011" data-mp="302bpct479rmf">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2012" data-mp="2v4a6a86rxsay">
                      Presenting Complaint
                    </h3>
                    <div className="p-3 min-h-[80px] whitespace-pre-wrap" data-id="element-2013" data-mp="2q3nkyx551b17">
                      {enc.clinicalNote?.presentingComplaint || ''}
                    </div>
                  </div>

                  <div className="border-2 border-black" data-id="element-2014" data-mp="2h63e98oiamj7">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2015" data-mp="kcs6g8gsuehm">
                      History of Presenting Complaint
                    </h3>
                    <div className="p-3 min-h-[80px] whitespace-pre-wrap" data-id="element-2016" data-mp="fbb386auefxv">
                      {enc.clinicalNote?.historyOfPresentingComplaint || ''}
                    </div>
                  </div>

                  {/* PAST MEDICAL HISTORY */}
                  <div className="border-2 border-black" data-id="element-2017" data-mp="wnj7u09cp3fi">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2018" data-mp="11lkwd0pjpuyd">
                      Past Medical History / General Co-Morbidities
                    </h3>
                    <table className="w-full border-collapse text-sm" data-id="element-2019" data-mp="8wvrcqldd5hc">
                      <tbody data-id="element-2020" data-mp="8yzx7e2twek9">
                        {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA', 'RVD'].map(condition => <tr key={condition} className="border-b border-gray-300" data-id="element-2021" data-mp="3qdd2ft2wg2cz">
                            <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-2022" data-mp="3le0iph7brbem">
                              {condition}
                            </td>
                            <td className="p-2 text-center" data-id="element-2023" data-mp="3ug1ee9mwtpe6">
                              {enc.clinicalNote?.pastMedicalHistory?.includes(condition) ? '✓ Yes' : ''}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* DRUG HISTORY */}
                  <div className="border-2 border-black" data-id="element-2024" data-mp="2nxursk9lxx9r">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2025" data-mp="2izt6dsopllv4">
                      Drug History
                    </h3>
                    <div className="p-3 min-h-[40px]" data-id="element-2026" data-mp="yg7m6815l260">
                      {enc.clinicalNote?.drugHistory || ''}
                    </div>
                    <div className="p-3 border-t border-gray-300" data-id="element-2027" data-mp="ph2p7658pgvy">
                      <span className="font-semibold" data-id="element-2028" data-mp="pixailkfbb0k">Any known allergy:</span>{' '}
                      <span className="text-red-600 font-medium" data-id="element-2029" data-mp="ugytz7lrhcck">
                        {selectedPatient.allergies}
                      </span>
                    </div>
                  </div>

                  {/* FAMILY MEDICAL HISTORY */}
                  <div className="border-2 border-black" data-id="element-2030" data-mp="187kf72yotydm">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2031" data-mp="18728fceeg1rg">
                      Family Medical History
                    </h3>
                    <table className="w-full border-collapse text-sm" data-id="element-2032" data-mp="3swrmvob6xljm">
                      <tbody data-id="element-2033" data-mp="1r8dwt0kk0dt">
                        {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA'].map(condition => <tr key={condition} className="border-b border-gray-300" data-id="element-2034" data-mp="39qhxypk6szi">
                            <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-2035" data-mp="zz7w722lxnct">
                              {condition}
                            </td>
                            <td className="p-2 text-center" data-id="element-2036" data-mp="qyjdvv66l0u4">
                              {enc.clinicalNote?.familyMedicalHistory?.includes(condition) ? '✓ Yes' : ''}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* OBSTETRICS AND GYNAECOLOGY HISTORY */}
                  <div className="border-2 border-black" data-id="element-2037" data-mp="31m4svmflr5tf">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2038" data-mp="36k6cq9kur95y">
                      Obstetrics and Gynaecology History
                    </h3>
                    <div className="p-3 min-h-[60px] whitespace-pre-wrap" data-id="element-2039" data-mp="3fd1kb9xslgyw">
                      {enc.clinicalNote?.obgynHistory || ''}
                    </div>
                  </div>

                  {/* SOCIAL HISTORY */}
                  <div className="border-2 border-black" data-id="element-2040" data-mp="3paxu9yezu5vf">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2041" data-mp="3p9d0wc4f0sz5">
                      Social History
                    </h3>
                    <table className="w-full border-collapse text-sm" data-id="element-2042" data-mp="1uyr6m04wxwm8">
                      <tbody data-id="element-2043" data-mp="1zvho8rwd909r">
                        <tr className="border-b border-gray-300" data-id="element-2044" data-mp="1zwszfcs55aaw">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-2045" data-mp="1rus5mmrp4gfz">
                            Alcohol
                          </td>
                          <td className="p-2" data-id="element-2046" data-mp="1myuyn72bomfv">
                            {enc.clinicalNote?.socialHistory?.alcohol || ''}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-300" data-id="element-2047" data-mp="1wtd5um6y704f">
                          <td className="border-r border-gray-300 p-2 font-medium" data-id="element-2048" data-mp="2ate742r4k9kf">
                            Smoking
                          </td>
                          <td className="p-2" data-id="element-2049" data-mp="25tiawpoo5mff">
                            {enc.clinicalNote?.socialHistory?.smoking || ''}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-300" data-id="element-2050" data-mp="2fn8ylscz1su2">
                          <td className="border-r border-gray-300 p-2 font-medium" data-id="element-2051" data-mp="2tlyskz4hvce7">
                            Occupation
                          </td>
                          <td className="p-2" data-id="element-2052" data-mp="387mh0whqd32v">
                            {enc.clinicalNote?.socialHistory?.occupation || ''}
                          </td>
                        </tr>
                        <tr data-id="element-2053" data-mp="3d4cwbhlpnun8">
                          <td className="border-r border-gray-300 p-2 font-medium" data-id="element-2054" data-mp="2z7h9mmcje4op">
                            Mental Status
                          </td>
                          <td className="p-2" data-id="element-2055" data-mp="3447rnf7wjacr">
                            {enc.clinicalNote?.socialHistory?.mentalStatus || ''}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* REVIEW OF SYSTEMS */}
                  <div className="border-2 border-black" data-id="element-2056" data-mp="2khjran1ag97f">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2057" data-mp="2kfyxx0qpmwb5">
                      Review of Systems
                    </h3>
                    <div className="p-3 space-y-1" data-id="element-2058" data-mp="2bg24kltdl99r">
                      <p data-id="element-2059" data-mp="2qzekwl8jamze">
                        <span className="font-semibold" data-id="element-2060" data-mp="2qzmx0awu1o9j">R/S:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.rs || ''}
                      </p>
                      <p data-id="element-2061" data-mp="2hyonjd3mkojk">
                        <span className="font-semibold" data-id="element-2062" data-mp="2hxx6d7arytct">CVS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.cvs || ''}
                      </p>
                      <p data-id="element-2063" data-mp="280inz25ajuhv">
                        <span className="font-semibold" data-id="element-2064" data-mp="280sgawdt6dqq">CNS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.cns || ''}
                      </p>
                      <p data-id="element-2065" data-mp="237zeag9q5jic">
                        <span className="font-semibold" data-id="element-2066" data-mp="1ya7l7ixcfrck">GIT:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.git || ''}
                      </p>
                      <p data-id="element-2067" data-mp="1u7axjdsy551r">
                        <span className="font-semibold" data-id="element-2068" data-mp="1pakgan5eo3er">GUT:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.gut || ''}
                      </p>
                      <p data-id="element-2069" data-mp="1kdak8onigthy">
                        <span className="font-semibold" data-id="element-2070" data-mp="3vyt7y9pqy3bd">MSS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.mss || ''}
                      </p>
                      <p data-id="element-2071" data-mp="4ttgiu0v54j2">
                        <span className="font-semibold" data-id="element-2072" data-mp="9rcwpzh4frdn">IS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.is || ''}
                      </p>
                    </div>
                  </div>

                  {/* ON EXAMINATION */}
                  <div className="border-2 border-black" data-id="element-2073" data-mp="3d9mvh4fjq7y0">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2074" data-mp="3i63mq7i1l2fb">
                      On Examination
                    </h3>
                    <div className="p-3 space-y-2 text-sm" data-id="element-2075" data-mp="3m6nhj4f57tya">
                      {([{
                  key: 'gc',
                  label: 'G/C'
                }, {
                  key: 'febrile',
                  label: 'Temperature'
                }, {
                  key: 'pjc',
                  label: 'P | J | C'
                }, {
                  key: 's1s2',
                  label: 'S1  S2'
                }, {
                  key: 'rs',
                  label: 'R/S'
                }, {
                  key: 'pa',
                  label: 'P/A'
                }] as const).map(field => <div key={field.key} className="grid grid-cols-[6rem_1fr] gap-2" data-id="element-2076" data-mp="1lg45x0kjgi7h">
                          <span className="font-bold" data-id="element-2077" data-mp="1bipwvm0y2vp7">{field.label}:</span>
                          <span className="border-b border-gray-300 pb-0.5" data-id="element-2078" data-mp="1bku001r0rqof">
                            {enc.clinicalNote?.onExaminationDetails?.[field.key] || '\u00A0'}
                          </span>
                        </div>)}
                      {enc.clinicalNote?.onExamination && <div className="pt-2 mt-2 border-t border-gray-200" data-id="element-2079" data-mp="wufv18e4zvae">
                          <p className="text-xs font-bold uppercase text-gray-600 mb-1" data-id="element-2080" data-mp="1auqlwbfg59vk">
                            Additional Notes
                          </p>
                          <p className="whitespace-pre-wrap" data-id="element-2081" data-mp="1klljgby57vwb">
                            {enc.clinicalNote.onExamination}
                          </p>
                        </div>}
                    </div>
                  </div>

                  {/* SUMMARY OF SIGNIFICANT FINDINGS */}
                  <div className="border-2 border-black" data-id="element-2082" data-mp="1pky3yq1jkqvq">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2083" data-mp="1pmiywgrrqbu4">
                      Summary of Significant Findings
                    </h3>
                    <div className="p-3 min-h-[60px]" data-id="element-2084" data-mp="21nr523ratfka">
                      {enc.clinicalNote?.significantFindings?.filter(f => f.trim()).length ? <ol className="list-decimal pl-5 space-y-1" data-id="element-2085" data-mp="2bdkbkqcl34ho">
                          {enc.clinicalNote.significantFindings.filter(f => f.trim()).map((finding, i) => <li key={i} data-id="element-2086" data-mp="2pck3q7zb21il" data-mpi={i}>{finding}</li>)}
                        </ol> : <div className="space-y-2 text-gray-400" data-id="element-2087" data-mp="2ualkund67qrf">
                          {[1, 2, 3, 4, 5, 6, 7, 8].map(n => <p key={n} data-id="element-2088" data-mp="v0dei59ktk7a">
                              {n}.
                              ______________________________________________________________________________________
                            </p>)}
                        </div>}
                    </div>
                  </div>

                  {/* WORKING DIAGNOSIS */}
                  <div className="border-2 border-black" data-id="element-2089" data-mp="iokrjf2duqdf">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2090" data-mp="dpzshiyvacqk">
                      Working Diagnosis
                    </h3>
                    <div className="p-3 min-h-[60px]" data-id="element-2091" data-mp="1bambvtuqxyhq">
                      {enc.clinicalNote?.workingDiagnosis?.filter(d => d.trim()).length ? <ol className="list-decimal pl-5 space-y-1" data-id="element-2092" data-mp="11gm0xbcs9z13">
                          {enc.clinicalNote.workingDiagnosis.filter(d => d.trim()).map((dx, i) => <li key={i} className="font-bold" data-id="element-2093" data-mp="3h5nnkzdiuxxz" data-mpi={i}>
                                {dx}
                              </li>)}
                        </ol> : <div className="space-y-2 text-gray-400" data-id="element-2094" data-mp="1em8et7f0vdx4">
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <p key={n} data-id="element-2095" data-mp="zupskrvkkn5b">
                              {n}.
                              ______________________________________________________________________________________
                            </p>)}
                        </div>}
                    </div>
                  </div>

                  {/* INVESTIGATIONS TO BE DONE */}
                  <div className="border-2 border-black" data-id="element-2096" data-mp="1nm4z6wudpqmd">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2097" data-mp="1nkk2p1oi7xlv">
                      Investigations To Be Done
                    </h3>
                    <div className="p-4" data-id="element-2098" data-mp="c814tpjvq0gs">
                      <h4 className="font-bold text-sm uppercase mb-2 border-b border-gray-400 pb-1" data-id="element-2099" data-mp="c4vgicj2r2m4">
                        Laboratory Tests
                      </h4>
                      <table className="w-full border-collapse text-sm mb-4" data-id="element-2100" data-mp="2leuwyh1ybbc1">
                        <tbody data-id="element-2101" data-mp="2zfp1uppsiu5y">
                          {[['FBC', 'GRAVINDEX'], ['MPS/RDT', 'HVS'], ['CREATININE / U&E', 'URINALYSIS'], ['LIVER FUNCTION TEST', 'PAP SMEAR'], ['AAFB', 'CD4 COUNT'], ['URINE MICROSCOPY', 'URETHRAL SWAB'], ['VBG', 'VIRAL LOAD'], ['HEPATITIS PROFILE', 'RBS / FBS'], ['ABG', 'STOOL MICROSCOPY'], ['HORMONAL PROFILE', 'ANTI-MÜLLERIAN HORMONE'], ['RPR', 'HIV'], ['SPERM ANALYSIS', 'TESTOSTERONE'], ['DOA 6', '']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-2102" data-mp="3j1kmpa5vsp20" data-mpi={idx}>
                              {pair.map((test, ci) => {
                        if (!test) {
                          return <Fragment key={`empty-${ci}`} data-id="element-2103" data-mp="1ngpkg4b71is">
                                      <td className="p-1.5 border-r border-gray-200 w-1/3" data-id="element-2104" data-mp="1ortcfxr899w"></td>
                                      <td className="p-1.5 w-1/6 border-r border-gray-300" data-id="element-2105" data-mp="306xcry70c15m"></td>
                                    </Fragment>;
                        }
                        const isOrdered = enc.labRequests.some(lab => lab.tests.some(t => t.toLowerCase().includes(test.toLowerCase()) || test.toLowerCase().includes(t.toLowerCase())));
                        return <Fragment key={test} data-id="element-2106" data-mp="205910y2b6myl" data-mpi={ci}>
                                    <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-2107" data-mp="202mtgyff47gd">
                                      {test}
                                    </td>
                                    <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-2108" data-mp="1r2raw8jj9a6b">
                                      {isOrdered ? <span className="text-green-700 font-bold" data-id="element-2109" data-mp="1h6oap888rlm0">
                                          ✓
                                        </span> : ''}
                                    </td>
                                  </Fragment>;
                      })}
                            </tr>)}
                        </tbody>
                      </table>

                      <h4 className="font-bold text-sm uppercase mb-2 border-b border-gray-400 pb-1" data-id="element-2110" data-mp="2fntsbdf3ahd9">
                        Radiology Scans
                      </h4>
                      <table className="w-full border-collapse text-sm mb-4" data-id="element-2111" data-mp="21mr1yoh2akwj">
                        <tbody data-id="element-2112" data-mp="26nwxsv60qoaa">
                          {[['PELVIC SCAN', 'ANOMALY SCAN'], ['OBS SCAN', 'CXR (CHEST X-RAY)'], ['HSG (HYSTEROSALPINGOGRAPHY)', '']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-2113" data-mp="3m5733nekstso" data-mpi={idx}>
                              {pair.map((scan, ci) => {
                        if (!scan) {
                          return <Fragment key={`empty-r-${ci}`} data-id="element-2114" data-mp="1fdkdr5win8uy">
                                      <td className="p-1.5 border-r border-gray-200 w-1/3" data-id="element-2115" data-mp="1ahvbuvx0xfvh"></td>
                                      <td className="p-1.5 w-1/6 border-r border-gray-300" data-id="element-2116" data-mp="1p6869oggqqnv"></td>
                                    </Fragment>;
                        }
                        const isOrdered = enc.radiologyRequests.some(r => {
                          const a = r.scanType.toLowerCase();
                          const b = scan.toLowerCase();
                          return a.includes(b) || b.includes(a);
                        });
                        return <Fragment key={scan} data-id="element-2117" data-mp="8r6pfsdx7kaz" data-mpi={ci}>
                                    <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-2118" data-mp="8sri1cgoct67">
                                      {scan}
                                    </td>
                                    <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-2119" data-mp="15r978gp9k9gb">
                                      {isOrdered ? <span className="text-green-700 font-bold" data-id="element-2120" data-mp="1eqvy48kd9wv4">
                                          ✓
                                        </span> : ''}
                                    </td>
                                  </Fragment>;
                      })}
                            </tr>)}
                        </tbody>
                      </table>

                      <div className="border-t border-gray-300 pt-2" data-id="element-2121" data-mp="2cca81fuzkq5p">
                        <span className="font-semibold" data-id="element-2122" data-mp="27gl5r4rl0v5p">
                          Other investigations:
                        </span>{' '}
                        {enc.labRequests.map(l => l.otherTests).filter(Boolean).join(', ') || ''}
                      </div>

                      {/* Investigation Results — View File list */}
                      {(enc.labRequests.some(l => l.status === 'Completed') || enc.radiologyRequests.some(r => r.status === 'Completed')) && <div className="mt-4 pt-4 border-t-2 border-black" data-id="element-2123" data-mp="1ycee8bp7ch5w">
                          <h4 className="font-bold mb-3 uppercase text-center" data-id="element-2124" data-mp="1yabdawmih3me">
                            Investigation Results
                          </h4>
                          <table className="w-full border-collapse text-sm" data-id="element-2125" data-mp="1of9pc43z27le">
                            <thead data-id="element-2126" data-mp="1tdupk3j84r9u">
                              <tr className="bg-gray-100" data-id="element-2127" data-mp="1tctcxumk7wq0">
                                <th className="border border-black p-1.5 text-left" data-id="element-2128" data-mp="1a981o3stf2e9">
                                  Test / Scan
                                </th>
                                <th className="border border-black p-1.5 text-left" data-id="element-2129" data-mp="15g547kkx38ss">
                                  Type
                                </th>
                                <th className="border border-black p-1.5 text-left" data-id="element-2130" data-mp="vl4skljh7al8">
                                  Performed By
                                </th>
                                <th className="border border-black p-1.5 text-left" data-id="element-2131" data-mp="1o7xz5kjjr435">
                                  Completed
                                </th>
                                <th className="border border-black p-1.5 text-center w-32 print:hidden" data-id="element-2132" data-mp="1jb7j2x7qs8hq">
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody data-id="element-2133" data-mp="2u39i9cnwuwf">
                              {enc.labRequests.filter(l => l.status === 'Completed').flatMap(lab => lab.tests.filter(testName => !(lab.removedTestNames || []).includes(testName)).map(testName => <tr key={`${lab.id}-${testName}`} data-id="element-2134" data-mp="32697gmcad6fk">
                                        <td className="border border-black p-1.5 font-medium" data-id="element-2135" data-mp="37870g8ahnflp">
                                          {testName}
                                        </td>
                                        <td className="border border-black p-1.5" data-id="element-2136" data-mp="1ctxjnh3ykx3k">
                                          Laboratory
                                        </td>
                                        <td className="border border-black p-1.5" data-id="element-2137" data-mp="388taxd5re27e">
                                          —
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-2138" data-mp="33cm1hae5m8kp">
                                          {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                                        </td>
                                        <td className="border border-black p-1.5 text-center print:hidden" data-id="element-2139" data-mp="2yboordbwy6ql">
                                          <Button size="sm" variant="outline" onClick={() => setViewingTest({
                            kind: 'lab',
                            encounterId: enc.id,
                            labId: lab.id,
                            testName
                          })} data-id="element-2140" data-mp="2tk6iqav19fyr">
                                            <Eye className="w-3.5 h-3.5 mr-1" data-id="element-2141" data-mp="2ag35r7vuxf7p" />{' '}
                                            View File
                                          </Button>
                                        </td>
                                      </tr>))}
                              {enc.labRequests.filter(l => l.status === 'Completed' && l.otherTests).map(lab => <tr key={`${lab.id}-other`} data-id="element-2142" data-mp="2wmxfwodrn11i">
                                    <td className="border border-black p-1.5 font-medium" data-id="element-2143" data-mp="2rpe3zv4jj0cq">
                                      {lab.otherTests}{' '}
                                      <span className="text-xs text-gray-500" data-id="element-2144" data-mp="2mwkxb1pihhy0">
                                        (Other)
                                      </span>
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-2145" data-mp="2cy53us30hwlc">
                                      Laboratory
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-2146" data-mp="240co5mtdomxf">
                                      —
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-2147" data-mp="27y7ss6hfqj4">
                                      {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                                    </td>
                                    <td className="border border-black p-1.5 text-center print:hidden" data-id="element-2148" data-mp="eiphd796tls3">
                                      <Button size="sm" variant="outline" onClick={() => setViewingTest({
                            kind: 'lab',
                            encounterId: enc.id,
                            labId: lab.id
                          })} data-id="element-2149" data-mp="ji24lt3z0uv7">
                                        <Eye className="w-3.5 h-3.5 mr-1" data-id="element-2150" data-mp="1c66ekqtkru3p" />{' '}
                                        View File
                                      </Button>
                                    </td>
                                  </tr>)}
                              {enc.radiologyRequests.filter(r => r.status === 'Completed').map(rad => <tr key={rad.id} data-id="element-2151" data-mp="227qrcs7fr8jk">
                                    <td className="border border-black p-1.5 font-medium" data-id="element-2152" data-mp="2g071ecyr86tz">
                                      {rad.scanType}
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-2153" data-mp="2pywcnmw5at19">
                                      Radiology
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-2154" data-mp="2l0mautfx0u6o">
                                      {rad.radiographerName || '—'}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-2155" data-mp="qnd0z0jgj6nw">
                                      {rad.completedAt ? new Date(rad.completedAt).toLocaleString() : ''}
                                    </td>
                                    <td className="border border-black p-1.5 text-center print:hidden" data-id="element-2156" data-mp="2xgm29fjsdyjs">
                                      <Button size="sm" variant="outline" onClick={() => setViewingTest({
                            kind: 'rad',
                            encounterId: enc.id,
                            radId: rad.id
                          })} data-id="element-2157" data-mp="2jhcmui3en8gv">
                                        <Eye className="w-3.5 h-3.5 mr-1" data-id="element-2158" data-mp="1e24vszlyxf2h" />{' '}
                                        View File
                                      </Button>
                                    </td>
                                  </tr>)}
                            </tbody>
                          </table>
                        </div>}
                    </div>
                  </div>

                  {/* DRUG TREATMENT / PRESCRIPTION */}
                  <div className="border-2 border-black" data-id="element-2159" data-mp="21ouxfzuen7mk">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2160" data-mp="25rhwakphtst9">
                      Drug Treatment / Prescription
                    </h3>
                    <table className="w-full border-collapse text-sm text-center" data-id="element-2161" data-mp="be8fe7umb50z">
                      <thead data-id="element-2162" data-mp="bgcullxyvqgk">
                        <tr className="bg-gray-100" data-id="element-2163" data-mp="gcjsqsk4fxny">
                          <th className="border border-black p-1.5 w-10" data-id="element-2164" data-mp="gf5ycmngc33j">
                            No.
                          </th>
                          <th className="border border-black p-1.5 text-left" data-id="element-2165" data-mp="42byrnjlgepu">
                            Drug Name
                          </th>
                          <th className="border border-black p-1.5" data-id="element-2166" data-mp="3v9epkdvisizj">Dose</th>
                          <th className="border border-black p-1.5" data-id="element-2167" data-mp="3qb3iuc0iuolj">Route</th>
                          <th className="border border-black p-1.5" data-id="element-2168" data-mp="rsy3b4ue3gv3">
                            Frequency
                          </th>
                          <th className="border border-black p-1.5" data-id="element-2169" data-mp="mtuzfnq5bgop">
                            No. of Days
                          </th>
                          <th className="border border-black p-1.5" data-id="element-2170" data-mp="dsnb9yhcfw8c">
                            Signature
                          </th>
                        </tr>
                      </thead>
                      <tbody data-id="element-2171" data-mp="2yiw0961gh24i">
                        {enc.prescriptions.length > 0 ? enc.prescriptions.map((rx, i) => <tr key={rx.id} data-id="element-2172" data-mp="301coj0dfcylo" data-mpi={i}>
                                <td className="border border-black p-1.5" data-id="element-2173" data-mp="34z4lw9wu9cx9">
                                  {i + 1}
                                </td>
                                <td className="border border-black p-1.5 text-left font-medium" data-id="element-2174" data-mp="2q1c4fc7qeegf">
                                  {rx.drugName}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-2175" data-mp="2h8ikh1hcaqw8">
                                  {rx.dose}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-2176" data-mp="27bdz33vbizgh">
                                  {rx.route}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-2177" data-mp="2caywvayr51rm">
                                  {rx.frequency}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-2178" data-mp="22cswwywia5pe">
                                  {rx.days}
                                </td>
                                <td className="border border-black p-1.5 italic text-xs" data-id="element-2179" data-mp="1jq398kpcs2yr">
                                  {enc.lastAttendingClinician || ''}
                                </td>
                              </tr>) : [1, 2, 3, 4, 5, 6, 7].map(i => <tr key={i} data-id="element-2180" data-mp="22muz5czehgbs">
                                <td className="border border-black p-3" data-id="element-2181" data-mp="22ljxpjtfgknu">{i}</td>
                                <td className="border border-black p-3" data-id="element-2182" data-mp="1xojqv6or7dvo"></td>
                                <td className="border border-black p-3" data-id="element-2183" data-mp="2bf58yt6dvjsi"></td>
                                <td className="border border-black p-3" data-id="element-2184" data-mp="2bh9bb6omwaqo"></td>
                                <td className="border border-black p-3" data-id="element-2185" data-mp="27e39qccgv7qj"></td>
                                <td className="border border-black p-3" data-id="element-2186" data-mp="2lee3bn55v0fe"></td>
                                <td className="border border-black p-3" data-id="element-2187" data-mp="2gerq3dpc1k6l"></td>
                              </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* STAT DOSES ADMINISTERED BY NURSING */}
                  {enc.statDoses && enc.statDoses.length > 0 && <div className="border-2 border-black" data-id="element-2188" data-mp="3997m24god61o">
                      <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2189" data-mp="3e80rrappzsra">
                        Stat Doses Administered (Nursing)
                      </h3>
                      <table className="w-full border-collapse text-sm" data-id="element-2190" data-mp="2lik40qd1p01e">
                        <thead data-id="element-2191" data-mp="2qi6g2wh50a8m">
                          <tr className="bg-gray-100" data-id="element-2192" data-mp="2qhwzfzhzfffl">
                            <th className="border border-black p-1.5 w-10" data-id="element-2193" data-mp="2qkj7e08sbwyc">
                              No.
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-2194" data-mp="5sqs099ayzmo">
                              Drug Name
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-2195" data-mp="asvceadc0ei6">
                              Dose
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-2196" data-mp="1qb1cc56javpy">
                              Route
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-2197" data-mp="1lgx4ljpg2jnt">
                              Time Given
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-2198" data-mp="1cjmq7phbyeeo">
                              Nurse
                            </th>
                          </tr>
                        </thead>
                        <tbody data-id="element-2199" data-mp="12l7460x5tvc1">
                          {enc.statDoses.map((dose, idx) => <tr key={dose.id} data-id="element-2200" data-mp="xmm90fwrm1ug" data-mpi={idx}>
                              <td className="border border-black p-1.5 text-center" data-id="element-2201" data-mp="2wygbpn65b6rn">
                                {idx + 1}
                              </td>
                              <td className="border border-black p-1.5 font-medium" data-id="element-2202" data-mp="2rzvg611u9b9j">
                                {dose.drugName}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2203" data-mp="v169pqsonhay">
                                {dose.dose}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2204" data-mp="ga5mugr6vkva">
                                {dose.route}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2205" data-mp="191pe8nxxw58d">
                                {dose.givenAt ? new Date(dose.givenAt).toLocaleString() : ''}
                              </td>
                              <td className="border border-black p-1.5 italic text-xs" data-id="element-2206" data-mp="141b32x11kf64">
                                {dose.nurseName || ''}
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>}

                  {/* FOLLOW UP / REVIEW DATE */}
                  <div className="border-2 border-black" data-id="element-2207" data-mp="3c9g8tywnr859">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2208" data-mp="3c8y3mcs0pjl7">
                      Next Follow Up / Review Date (If Any)
                    </h3>
                    <table className="w-full border-collapse text-sm text-center" data-id="element-2209" data-mp="3m7x3p6ypw6wl">
                      <thead data-id="element-2210" data-mp="2yiukhuofq5zz">
                        <tr className="bg-gray-100" data-id="element-2211" data-mp="2yfoxqm3a3g7f">
                          <th className="border border-black p-1 w-10" data-id="element-2212" data-mp="2tjhqmpz5bynw">No.</th>
                          <th className="border border-black p-1" data-id="element-2213" data-mp="387ckwassyb33">Date</th>
                          <th className="border border-black p-1 w-10" data-id="element-2214" data-mp="389fjhj80tcjf">No.</th>
                          <th className="border border-black p-1" data-id="element-2215" data-mp="33fl24mfw19ni">Date</th>
                          <th className="border border-black p-1 w-10" data-id="element-2216" data-mp="2fq9zezez956q">No.</th>
                          <th className="border border-black p-1" data-id="element-2217" data-mp="2asqlk0m6yufb">Date</th>
                        </tr>
                      </thead>
                      <tbody data-id="element-2218" data-mp="1pagn2r71zxg6">
                        {[0, 1, 2, 3, 4].map(row => <tr key={row} data-id="element-2219" data-mp="1u8rwx1xcm7ye">
                            <td className="border border-black p-1 font-medium" data-id="element-2220" data-mp="1u9tku6zgqoxj">
                              {row + 1}
                            </td>
                            <td className="border border-black p-2" data-id="element-2221" data-mp="239q6dzqk37od">
                              {row === 0 && enc.reviewDate ? new Date(enc.reviewDate).toLocaleDateString() : ''}
                            </td>
                            <td className="border border-black p-1 font-medium" data-id="element-2222" data-mp="2hx1gryb04lmw">
                              {row + 6}
                            </td>
                            <td className="border border-black p-2" data-id="element-2223" data-mp="2myqpv8enyp8c"></td>
                            <td className="border border-black p-1 font-medium" data-id="element-2224" data-mp="2qzkc7xcgjvz4">
                              {row + 11}
                            </td>
                            <td className="border border-black p-2" data-id="element-2225" data-mp="k9qvh0okn5c1"></td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* NAME OF MEDICAL PRACTITIONER */}
                  <div className="border-2 border-black p-4 mt-6" data-id="element-2226" data-mp="b9khldoyo3cc">
                    <p className="font-bold text-sm" data-id="element-2227" data-mp="6b9ahaq1w6xt">
                      Name of the Medical Practitioner:{' '}
                      <span className="font-normal border-b border-black inline-block min-w-[250px] ml-2" data-id="element-2228" data-mp="12zyub6l8n9mf">
                        {enc.lastAttendingClinician || ''}
                      </span>
                    </p>
                  </div>
                </div>
              </div>) : <Card data-id="element-2229" data-mp="p8tplq30jr58">
              <p className="text-gray-500 text-center py-8" data-id="element-2230" data-mp="p793mourlgiz">
                No encounters found for this patient.
              </p>
            </Card>}
        </div>

        {/* Amendment Reason Modal */}
        {amendModal.isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-2231" data-mp="1zm0wgc6ybaoz">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-2232" data-mp="1ul3jce0st6uc">
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-2233" data-mp="1pm0eatktj0md">
                Amend Completed Visit
              </h3>
              <p className="text-sm text-gray-600 mb-4" data-id="element-2234" data-mp="1kp9wa0pgduyb">
                Please provide a reason for amending this completed file. Your
                name, the timestamp, and this reason will be recorded in the
                audit trail.
              </p>
              <textarea className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none" rows={4} value={amendModal.reason} onChange={e => setAmendModal({
            ...amendModal,
            reason: e.target.value
          })} placeholder="e.g., Correction of working diagnosis after MDT discussion" data-id="element-2235" data-mp="28l5uziiij415" />
              <div className="flex justify-end space-x-3 mt-4" data-id="element-2236" data-mp="38mj3mzd8v97z">
                <Button variant="outline" onClick={() => setAmendModal({
              isOpen: false,
              encounterId: '',
              reason: ''
            })} data-id="element-2237" data-mp="3dkkmpkao78jg">
                  Cancel
                </Button>
                <Button variant="primary" className="bg-amber-600 hover:bg-amber-700 text-white" disabled={!amendModal.reason.trim()} onClick={() => {
              navigate('/dashboard', {
                state: {
                  amendEncounterId: amendModal.encounterId,
                  amendReason: amendModal.reason.trim()
                }
              });
            }} data-id="element-2238" data-mp="3rj11zzmlm7d7">
                  Open in Consultation
                </Button>
              </div>
            </div>
          </div>}

        {viewingTest && (() => {
        const enc = encounters.find(e => e.id === viewingTest.encounterId);
        if (!enc) return null;
        const lab = viewingTest.kind === 'lab' ? enc.labRequests.find(l => l.id === viewingTest.labId) : null;
        const rad = viewingTest.kind === 'rad' ? enc.radiologyRequests.find(r => r.id === viewingTest.radId) : null;
        if (!lab && !rad) return null;
        return <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-id="element-2239" data-mp="d5p25voby81k">
                <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:max-w-none print:shadow-none print:rounded-none print:overflow-visible test-modal-print" data-id="element-2240" data-mp="2jwokau64lw6f">
                  <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10 print:hidden" data-id="element-2241" data-mp="zcjffl1jc9zl">
                    <h3 className="text-lg font-semibold text-gray-900" data-id="element-2242" data-mp="kndhe0efopta">
                      {lab ? 'Laboratory Result' : 'Radiology Result'}
                    </h3>
                    <div className="flex items-center space-x-2" data-id="element-2243" data-mp="bmyu4vhwt0n5">
                      <Button variant="outline" size="sm" onClick={handlePrintTestModal} data-id="element-2244" data-mp="fotunxs0n9zv">
                        <Printer className="w-4 h-4 mr-1" data-id="element-2245" data-mp="1r57gjah7qrn" /> Print
                      </Button>
                      <Button variant="primary" size="sm" onClick={handleDownloadTestPdf} disabled={isGeneratingTestPdf} data-id="element-2246" data-mp="6q86nn3rz2rp">
                        <Download className="w-4 h-4 mr-1" data-id="element-2247" data-mp="10f596x3yqgs" />
                        {isGeneratingTestPdf ? 'Generating...' : 'Download PDF'}
                      </Button>
                      <button onClick={closeTestModal} className="p-1.5 hover:bg-gray-100 rounded" aria-label="Close" data-id="element-2248" data-mp="3o4lco4bo6ulh">
                        <X className="w-5 h-5 text-gray-500" data-id="element-2249" data-mp="3e65mq4odprdk" />
                      </button>
                    </div>
                  </div>

                  <div ref={testModalRef} className="p-8 bg-white" data-id="element-2250" data-mp="39e5pwl3lvel7">
                    <div className="flex justify-between items-start mb-6 border-b-2 border-black pb-4" data-id="element-2251" data-mp="34fug2adb942z">
                      <div className="flex items-center" data-id="element-2252" data-mp="30ftt21c5bkkb">
                        <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED" className="h-16 w-16 object-contain mr-3" data-id="element-2253" data-mp="30d7ma3x2x935" />
                        <div data-id="element-2254" data-mp="15yaklt6qd42l">
                          <h1 className="text-xl font-bold text-pink-600 uppercase" data-id="element-2255" data-mp="1axwtxrjfu064">
                            BMED MEDICAL CENTRE
                          </h1>
                          <p className="text-xs italic text-gray-600" data-id="element-2256" data-mp="1fxhsw1yly8iu">
                            Caring Healing Leading
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-sm" data-id="element-2257" data-mp="na5gulq2pj3s">
                        <p className="font-bold uppercase border-b border-black pb-1 mb-2" data-id="element-2258" data-mp="s6m8hpwheflm">
                          {lab ? 'Laboratory Report' : 'Radiology Report'}
                        </p>
                        <p data-id="element-2259" data-mp="10zz6h69ek7ek">
                          <span className="font-semibold" data-id="element-2260" data-mp="111abf9el3l79">Patient:</span>{' '}
                          {selectedPatient.firstName} {selectedPatient.lastName}
                        </p>
                        <p data-id="element-2261" data-mp="8id5ektegp7c">
                          <span className="font-semibold" data-id="element-2262" data-mp="8hlfnqmhhloz">File No:</span>{' '}
                          {selectedPatient.bmcFileNumber}
                        </p>
                        <p data-id="element-2263" data-mp="16u7m5tgv039d">
                          <span className="font-semibold" data-id="element-2264" data-mp="16uz5o5x6maja">Age/Sex:</span>{' '}
                          {selectedPatient.age} / {selectedPatient.sex}
                        </p>
                        <p data-id="element-2265" data-mp="1gmdb0ubte4kc">
                          <span className="font-semibold" data-id="element-2266" data-mp="1gn58kaoz2cd9">Date:</span>{' '}
                          {new Date((lab?.completedAt || rad?.completedAt) ?? Date.now()).toLocaleString()}
                        </p>
                        {lab?.performedBy && <p data-id="element-2267" data-mp="xu18a6h9119o">
                            <span className="font-semibold" data-id="element-2268" data-mp="xszn396iqye8">Performed By:</span>{' '}
                            {lab.performedBy}
                          </p>}
                      </div>
                    </div>

                    {lab && (() => {
                const focusedTest = viewingTest?.kind === 'lab' ? viewingTest.testName : undefined;
                const showFBC = focusedTest && isFBCTest(focusedTest);
                const showUA = focusedTest && isUrinalysisTest(focusedTest);
                const showT = focusedTest && isTestosteroneTest(focusedTest);
                const showHP = focusedTest && isHormonalProfileTest(focusedTest);
                const showAMH = focusedTest && isAMHTest(focusedTest);
                const showHCG = focusedTest && isHCGTest(focusedTest);
                return <div data-id="element-2269" data-mp="3ihsypv8ih05x">
                            <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-2270" data-mp="3igrkjhw77xjz">
                              {focusedTest || 'Tests Performed'}
                            </h2>
                            {showFBC ? <div className="space-y-4" data-id="element-2271" data-mp="1igl9x4wb30if">
                                <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2272" data-mp="1if1svc2tslfl">
                                  <h3 className="font-bold uppercase" data-id="element-2273" data-mp="1ef9jwy9isd9p">
                                    Full Blood Count (FBC) Report
                                  </h3>
                                </div>
                                {fbcStructure.map(cat => <div key={cat.category} className="border border-black" data-id="element-2274" data-mp="225dlz17284yl">
                                    <div className="bg-gray-100 p-2 font-bold border-b border-black text-xs uppercase" data-id="element-2275" data-mp="1s5377c5dmphb">
                                      {cat.category}
                                    </div>
                                    <table className="w-full border-collapse text-sm" data-id="element-2276" data-mp="gpeiaf8kbgdv">
                                      <thead data-id="element-2277" data-mp="6odkmxk1zofp">
                                        <tr className="bg-gray-50" data-id="element-2278" data-mp="6l7xvoywcyn5">
                                          <th className="border border-black p-1.5 text-left" data-id="element-2279" data-mp="bmnjpj8wf96l">
                                            Test
                                          </th>
                                          <th className="border border-black p-1.5 text-left w-32" data-id="element-2280" data-mp="ldi3mgyo9wov">
                                            Result
                                          </th>
                                          <th className="border border-black p-1.5 text-left w-32" data-id="element-2281" data-mp="ubc1hwc34ye5">
                                            Reference Range
                                          </th>
                                          <th className="border border-black p-1.5 text-left w-24" data-id="element-2282" data-mp="14aayuireh7lu">
                                            Unit
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody data-id="element-2283" data-mp="1gbkq6czwpuzq">
                                        {cat.tests.map(t => <tr key={t.name} data-id="element-2284" data-mp="1l4nkwogf7ahi">
                                            <td className="border border-black p-1.5" data-id="element-2285" data-mp="17c7o45e448p1">
                                              {t.name}
                                            </td>
                                            <td className="border border-black p-1.5 font-bold" data-id="element-2286" data-mp="1zzttnj6whnej">
                                              {lab.results && lab.results[t.name] || '—'}
                                            </td>
                                            <td className="border border-black p-1.5 text-xs" data-id="element-2287" data-mp="1uzz017vqqjps">
                                              {t.range}
                                            </td>
                                            <td className="border border-black p-1.5 text-xs" data-id="element-2288" data-mp="jmwleid7c489">
                                              {t.unit}
                                            </td>
                                          </tr>)}
                                      </tbody>
                                    </table>
                                  </div>)}
                              </div> : showUA ? <div data-id="element-2289" data-mp="3ohwp9zkwu3qv">
                                <div className="text-center border-b-2 border-black pb-2 mb-6" data-id="element-2290" data-mp="3kixliiax0d10">
                                  <h3 className="font-bold uppercase" data-id="element-2291" data-mp="24y4nqafe3h7">
                                    Urinalysis Report
                                  </h3>
                                </div>
                                <div className="max-w-md mx-auto space-y-2" data-id="element-2292" data-mp="35szrvmu7uv4f">
                                  {urinalysisTests.map(t => {
                        const key = `UA_${t.name}`;
                        const value = lab.results && (lab.results[key] || lab.results[t.name]) || '';
                        return <div key={t.name} className="flex items-center text-sm" data-id="element-2293" data-mp="1jev2y0kthsdc">
                                        <span className="w-16 font-bold" data-id="element-2294" data-mp="qs0uy356slgr">
                                          {t.name}
                                        </span>
                                        <span className="mx-3 text-gray-400" data-id="element-2295" data-mp="zsfht6xsu8md">
                                          -
                                        </span>
                                        <span className="flex-1 border-b border-gray-400 pb-0.5 font-medium" data-id="element-2296" data-mp="2b7cdv0sehy38">
                                          {value || '\u00A0'}
                                        </span>
                                      </div>;
                      })}
                                </div>
                                <div className="max-w-md mx-auto mt-10 grid grid-cols-2 gap-4 text-sm" data-id="element-2297" data-mp="3muyomud0xqbw">
                                  <div data-id="element-2298" data-mp="3hvvui4yl14ip">
                                    <p className="font-semibold mb-2" data-id="element-2299" data-mp="3czexsmdbhhu0">
                                      Dr.'s Name:
                                    </p>
                                    <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-2300" data-mp="3825958xr5a78">
                                      {lab.results && lab.results['UA_DR_NAME'] || '\u00A0'}
                                    </p>
                                  </div>
                                  <div data-id="element-2301" data-mp="x8snlsoom08r">
                                    <p className="font-semibold mb-2" data-id="element-2302" data-mp="x5oau6x5hw6h">
                                      Signature:
                                    </p>
                                    <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-2303" data-mp="3v09jt7prjjx3">
                                      {lab.results && lab.results['UA_DR_SIG'] || '\u00A0'}
                                    </p>
                                  </div>
                                </div>
                              </div> : showT ? <div className="space-y-2" data-id="element-2304" data-mp="2ybbn3jygdbxa">
                                <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2305" data-mp="2ycwi1aooiwvo">
                                  <h3 className="font-bold uppercase" data-id="element-2306" data-mp="32d6pysw17xd6">
                                    Testosterone Report
                                  </h3>
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-2307" data-mp="2tb74nodzi74f">
                                  <thead data-id="element-2308" data-mp="29qyl260csmy6">
                                    <tr className="bg-gray-100" data-id="element-2309" data-mp="24r3rtvt3vl9y">
                                      <th className="border border-black p-1.5 text-left" data-id="element-2310" data-mp="24snblwdz0egh">
                                        Test
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-2311" data-mp="2eo8jgb5q08y5">
                                        Result
                                      </th>
                                      <th className="border border-black p-1.5 text-left" data-id="element-2312" data-mp="2nqq2kgq2wlgx">
                                        Reference (Male)
                                      </th>
                                      <th className="border border-black p-1.5 text-left" data-id="element-2313" data-mp="2xf81stipmkig">
                                        Reference (Female)
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-20" data-id="element-2314" data-mp="37eh0u0sfmpbo">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-2315" data-mp="3je44nbzep5tf">
                                    {testosteroneTests.map(t => <tr key={t.name} data-id="element-2316" data-mp="3t61jqttjchxf">
                                        <td className="border border-black p-1.5" data-id="element-2317" data-mp="3t7l1kouub11b">
                                          {t.name}
                                        </td>
                                        <td className="border border-black p-1.5 font-bold" data-id="element-2318" data-mp="60amgk3ltj17">
                                          {lab.results && lab.results[`TESTO_${t.name}`] || lab.results[t.name] || '—'}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-2319" data-mp="2hq36sev8lbqx">
                                          {t.rangeMale}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-2320" data-mp="u4i9au96ng9i">
                                          {t.rangeFemale}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-2321" data-mp="l4vjz6tqa0wt">
                                          {t.unit}
                                        </td>
                                      </tr>)}
                                  </tbody>
                                </table>
                              </div> : showHP ? <div data-id="element-2322" data-mp="13ts4wk38jh2d">
                                <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2323" data-mp="13wxqvqgki2tv">
                                  <h3 className="font-bold uppercase" data-id="element-2324" data-mp="3ti2g2lg2l2e8">
                                    Hormonal Profile Report
                                  </h3>
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-2325" data-mp="1skabu47bjg5g">
                                  <thead data-id="element-2326" data-mp="1xibvaq8nphhg">
                                    <tr className="bg-gray-100" data-id="element-2327" data-mp="1ik9p08a8qhwi">
                                      <th className="border border-black p-1.5 text-left" data-id="element-2328" data-mp="1ilumo6ruqgyl">
                                        Test Description
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-2329" data-mp="2b973s1f8twki">
                                        Value
                                      </th>
                                      <th className="border border-black p-1.5 text-left" data-id="element-2330" data-mp="21i2rj9gycptd">
                                        Reference Range
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-20" data-id="element-2331" data-mp="2u6hvn0yjpnpx">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-2332" data-mp="ikfy6mgtcvtk">
                                    {hormonalProfileTests.map(h => <Fragment key={h.key} data-id="element-2333" data-mp="x93u4tukn7k1">
                                        <tr className="align-top" data-id="element-2334" data-mp="s9r4608elkd8">
                                          <td className="border border-black p-1.5 font-medium" data-id="element-2335" data-mp="saj1bfhnfq5m">
                                            {h.name}
                                          </td>
                                          <td className="border border-black p-1.5 font-bold" data-id="element-2336" data-mp="3vs0zswbj7ktd">
                                            {lab.results && (lab.results[`HP_${h.key}`] || lab.results[h.name] || lab.results[h.key]) || '—'}
                                          </td>
                                          <td className="border border-black p-1.5 text-xs" data-id="element-2337" data-mp="tw4vc7gm466f">
                                            {h.ranges.map(r => <div key={r.label} data-id="element-2338" data-mp="12xw1fdf2ef0t">
                                                <span className="font-medium" data-id="element-2339" data-mp="12x2xslbek5mw">
                                                  {r.label}:
                                                </span>{' '}
                                                {r.value}
                                              </div>)}
                                          </td>
                                          <td className="border border-black p-1.5 text-xs" data-id="element-2340" data-mp="k0bbe30kdaem">
                                            {h.unit}
                                          </td>
                                        </tr>
                                        <tr data-id="element-2341" data-mp="3ip9zswuyws2b">
                                          <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-2342" data-mp="3ioh3kptmxkyf">
                                            Method: {h.method}
                                          </td>
                                        </tr>
                                      </Fragment>)}
                                  </tbody>
                                </table>
                              </div> : showAMH ? <div data-id="element-2343" data-mp="27xkeuql8i4j0">
                                <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2344" data-mp="27w9fd2ytnixp">
                                  <h3 className="font-bold uppercase" data-id="element-2345" data-mp="2cuaujct4my3w">
                                    Anti Mullerian Hormone (AMH) Report
                                  </h3>
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-2346" data-mp="11em76kbynx2k">
                                  <thead data-id="element-2347" data-mp="hdsz6xil4a64">
                                    <tr className="bg-gray-100" data-id="element-2348" data-mp="dd95y514tqp9">
                                      <th className="border border-black p-1.5 text-left" data-id="element-2349" data-mp="df3ppg0o3crr">
                                        Test Description
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-2350" data-mp="3mxodfoizizw">
                                        Value
                                      </th>
                                      <th className="border border-black p-1.5 text-left" data-id="element-2351" data-mp="3psuki2n8dpxf">
                                        Reference Range (by age, ng/mL)
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-20" data-id="element-2352" data-mp="3gvakex13tzoe">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-2353" data-mp="1mmnfj8w26aez">
                                    <tr className="align-top" data-id="element-2354" data-mp="1mllvwg0z8fln">
                                      <td className="border border-black p-1.5 font-medium" data-id="element-2355" data-mp="1rjx30izw0c06">
                                        {amhStructure.name}
                                      </td>
                                      <td className="border border-black p-1.5 font-bold" data-id="element-2356" data-mp="219qdths7gn3d">
                                        {lab.results && (lab.results[amhStructure.storageKey] || focusedTest && lab.results[focusedTest] || lab.results['AMH'] || lab.results['Anti-Müllerian Hormone'] || lab.results['ANTI-MÜLLERIAN HORMONE'] || lab.results['Anti Mullerian Hormone (AMH)']) || '—'}
                                      </td>
                                      <td className="border border-black p-1.5 text-xs" data-id="element-2357" data-mp="3aha4tdzc1qs6">
                                        {amhStructure.ageRanges.map(a => <div key={a.ageBand} data-id="element-2358" data-mp="3k8wolypqky6j">
                                            <span className="font-medium" data-id="element-2359" data-mp="3f9tkcghkyvzm">
                                              {a.ageBand}:
                                            </span>{' '}
                                            {a.range}
                                          </div>)}
                                      </td>
                                      <td className="border border-black p-1.5 text-xs" data-id="element-2360" data-mp="gqjq9uw9j6z3">
                                        {amhStructure.unit}
                                      </td>
                                    </tr>
                                    <tr data-id="element-2361" data-mp="7p2cwka8pje3">
                                      <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-2362" data-mp="7o0svqb8xmk8">
                                        Method: {amhStructure.method}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                                <div className="mt-3 border border-amber-300 bg-amber-50 rounded p-3 text-xs" data-id="element-2363" data-mp="367zi6lnafumw">
                                  <p className="font-bold uppercase mb-1" data-id="element-2364" data-mp="3b5smf9516ekj">
                                    Interpretation
                                  </p>
                                  <ul className="list-disc pl-5 space-y-0.5 text-gray-700" data-id="element-2365" data-mp="1byklzyvogtt0">
                                    {amhStructure.interpretation.map((line, i) => <li key={i} data-id="element-2366" data-mp="130ied0hfv2wr" data-mpi={i}>{line}</li>)}
                                  </ul>
                                </div>
                              </div> : showHCG ? <div data-id="element-2367" data-mp="1qn8chqqo2dc3">
                                <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2368" data-mp="1qqcsdldhuxil">
                                  <h3 className="font-bold uppercase" data-id="element-2369" data-mp="1lrt1r7nygxjw">
                                    Beta HCG (Quantitative) Report
                                  </h3>
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-2370" data-mp="6cudkvyd5n4">
                                  <thead data-id="element-2371" data-mp="4i9u7olffjk">
                                    <tr className="bg-gray-100" data-id="element-2372" data-mp="54mw6ec6n4hp">
                                      <th className="border border-black p-1.5 text-left" data-id="element-2373" data-mp="a4izscgywtwq">
                                        Test Description
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-2374" data-mp="ex2drajkhr22">
                                        Value
                                      </th>
                                      <th className="border border-black p-1.5 text-left" data-id="element-2375" data-mp="ny0ijv1ty2lp">
                                        Reference Range (mIU/mL)
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-20" data-id="element-2376" data-mp="xs0twenpg42v">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-2377" data-mp="15ckyhjblc27f">
                                    {(() => {
                          const hcgValue = lab.results && (lab.results[hcgStructure.storageKey] || focusedTest && lab.results[focusedTest] || lab.results['HCG'] || lab.results['Beta HCG']) || '';
                          const abnormal = isHCGAbnormal(hcgValue);
                          return <>
                                          <tr className="align-top" data-id="element-2378" data-mp="22ta5rsjb7wo7">
                                            <td className="border border-black p-1.5 font-medium" data-id="element-2379" data-mp="22v33ujfux8kl">
                                              {hcgStructure.name}
                                            </td>
                                            <td className={`border border-black p-1.5 font-bold ${abnormal ? 'text-red-600 bg-red-50' : ''}`} data-id="element-2380" data-mp="2f99lvihqdem0">
                                              {hcgValue || '—'}
                                              {abnormal && <div className="text-xs font-normal mt-0.5" data-id="element-2381" data-mp="2y1kgyq65889k">
                                                  Above normal threshold (&gt; 6
                                                  mIU/mL)
                                                </div>}
                                            </td>
                                            <td className="border border-black p-1.5 text-xs" data-id="element-2382" data-mp="3gtn14bhzu6ok">
                                              {hcgStructure.ranges.map(r => <div key={r.label} data-id="element-2383" data-mp="rlt1odxf6arz">
                                                  <span className="font-medium" data-id="element-2384" data-mp="rjyf6v6i2als">
                                                    {r.label}:
                                                  </span>{' '}
                                                  {r.range}
                                                </div>)}
                                            </td>
                                            <td className="border border-black p-1.5 text-xs" data-id="element-2385" data-mp="dmjpdpcc40ta">
                                              {hcgStructure.unit}
                                            </td>
                                          </tr>
                                          <tr data-id="element-2386" data-mp="1p16wlyxmnp61">
                                            <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-2387" data-mp="1u0jl0o45d4aq">
                                              Method: {hcgStructure.method}
                                            </td>
                                          </tr>
                                        </>;
                        })()}
                                  </tbody>
                                </table>
                              </div> : <>
                                <p className="mb-4 text-sm" data-id="element-2388" data-mp="2luoqgdx0a5m3">
                                  <span className="font-semibold" data-id="element-2389" data-mp="2gvlpp5o26ljx">
                                    Tests Ordered:
                                  </span>{' '}
                                  {lab.tests.filter(t => !(lab.removedTestNames || []).includes(t)).join(', ')}
                                  {lab.otherTests ? `, ${lab.otherTests}` : ''}
                                </p>
                                {lab.results && Object.keys(lab.results).length > 0 ? <table className="w-full border-collapse text-sm" data-id="element-2390" data-mp="2xa1ki67hoa44">
                                    <thead data-id="element-2391" data-mp="2ic93taq7hfoc">
                                      <tr className="bg-gray-100" data-id="element-2392" data-mp="2ibfxubz2mu79">
                                        <th className="border border-black p-2 text-left" data-id="element-2393" data-mp="2nast9l40d9lg">
                                          Parameter
                                        </th>
                                        <th className="border border-black p-2 text-left" data-id="element-2394" data-mp="1bvx61arapzta">
                                          Result
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody data-id="element-2395" data-mp="3l6xvzfbjimyt">
                                      {Object.entries(lab.results).map(([k, v]) => <tr key={k} data-id="element-2396" data-mp="3v4c7r1miqnks">
                                            <td className="border border-black p-2 font-medium" data-id="element-2397" data-mp="37d6tus0525d4">
                                              {k}
                                            </td>
                                            <td className="border border-black p-2" data-id="element-2398" data-mp="3g5ia5l1jgcfd">
                                              {v}
                                            </td>
                                          </tr>)}
                                    </tbody>
                                  </table> : <p className="text-gray-500 italic" data-id="element-2399" data-mp="2we4qbema5zfa">
                                    No structured results recorded for this
                                    test.
                                  </p>}

                                {lab.labComments && <div className="mt-6 border border-gray-300 bg-gray-50 rounded p-4" data-id="element-2400" data-mp="3b3kdylqiloqr">
                                    <h3 className="font-bold uppercase mb-2 text-sm" data-id="element-2401" data-mp="3g0jble9e0dtp">
                                      Comments / Observations / Interpretation
                                    </h3>
                                    <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800" data-id="element-2402" data-mp="3p2k1tr67dzlv">
                                      {lab.labComments}
                                    </pre>
                                  </div>}
                              </>}
                          </div>;
              })()}

                    {rad && <div data-id="element-2403" data-mp="1nc1qj7d8gs12">
                        <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-2404" data-mp="1nbtfzm4l1yt1">
                          {rad.scanType}
                        </h2>
                        {rad.radiographerName && <p className="text-sm mb-3" data-id="element-2405" data-mp="1dfqbi9m9do2x">
                            <span className="font-semibold" data-id="element-2406" data-mp="2b1w3doxjyglb">
                              Sonographer / Radiographer:
                            </span>{' '}
                            {rad.radiographerName}
                          </p>}
                        {rad.report && <div className="mb-4 border border-black p-4" data-id="element-2407" data-mp="1w559twak7z3m">
                            <pre className="whitespace-pre-wrap font-sans text-sm" data-id="element-2408" data-mp="1w79dcd4jqw3d">
                              {rad.report}
                            </pre>
                          </div>}
                        {rad.images && rad.images.length > 0 && <div data-id="element-2409" data-mp="38tkmkuowgwoi">
                            <div className="flex justify-between items-center mb-3 print:hidden" data-id="element-2410" data-mp="3cx0fph1rgewg">
                              <h3 className="font-bold" data-id="element-2411" data-mp="2yxrbzguqrd9d">Scan Images</h3>
                              <div className="flex items-center space-x-2 bg-gray-100 px-3 py-1.5 rounded-lg" data-id="element-2412" data-mp="2yzln63g7nr09">
                                <button onClick={() => setImageZoom(z => Math.max(10, z - 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom out" data-id="element-2413" data-mp="2p7poora7pyvi">
                                  <ZoomOut className="w-4 h-4" data-id="element-2414" data-mp="2l6ci2ezaprnj" />
                                </button>
                                <input type="range" min={10} max={200} step={10} value={imageZoom} onChange={e => setImageZoom(Number(e.target.value))} className="w-32" data-id="element-2415" data-mp="269lpaok4ne6w" />
                                <button onClick={() => setImageZoom(z => Math.min(200, z + 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom in" data-id="element-2416" data-mp="1wnkkbidbbzzh">
                                  <ZoomIn className="w-4 h-4" data-id="element-2417" data-mp="2fc923vgi1p71" />
                                </button>
                                <span className="text-sm font-medium w-12 text-right" data-id="element-2418" data-mp="2u9hyx4oknl65">
                                  {imageZoom}%
                                </span>
                              </div>
                            </div>
                            <div className="space-y-4 print:space-y-2" data-id="element-2419" data-mp="1ewdv023w2ql2">
                              {rad.images.map((img, idx) => <div key={idx} className="border-2 border-gray-300 rounded overflow-auto bg-gray-50 max-h-[600px] print:max-h-none print:overflow-visible" data-id="element-2420" data-mp="1jws1vgtr7uhi" data-mpi={idx}>
                                  <div style={{
                        transform: `scale(${imageZoom / 100})`,
                        transformOrigin: 'top left',
                        display: 'inline-block'
                      }} data-id="element-2421" data-mp="w5mor9f77gaw">
                                    <img src={img} alt={`${rad.scanType} ${idx + 1}`} className="block" data-id="element-2422" data-mp="8er1mplot99z" />
                                  </div>
                                </div>)}
                            </div>
                          </div>}
                      </div>}
                  </div>
                </div>
              </div>;
      })()}
      </div>;
  }
  // ─── PATIENT DIRECTORY ───
  return <div className="space-y-6" data-id="element-2423" data-mp="22zaqxd14fajo">
      <div className="flex justify-between items-center" data-id="element-2424" data-mp="22z2etncto99j">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2425" data-mp="1yxz0x6ec4nay">Patient Directory</h1>
      </div>

      <Card data-id="element-2426" data-mp="1u074q06nqf0y">
        <div className="relative" data-id="element-2427" data-mp="1u0qfmwxw435h">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2428" data-mp="1txkolc5par74">
            <Search className="h-5 w-5 text-gray-400" data-id="element-2429" data-mp="1ozt1cvg03t99" />
          </div>
          <input type="text" className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by name or BMC number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2430" data-mp="1k2sr08c4c4cc" />
        </div>
      </Card>

      <Card noPadding data-id="element-2431" data-mp="2yoln022ndkcg">
        <div className="overflow-x-auto" data-id="element-2432" data-mp="20zabhn1hui5q">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-2433" data-mp="210lbrcvqd7s3">
            <thead className="bg-gray-50" data-id="element-2434" data-mp="25xbrfz3mi1cz">
              <tr data-id="element-2435" data-mp="25ydel1xwyeb2">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2436" data-mp="25zy8cpcelt7v">
                  BMC Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2437" data-mp="2f0cxjzshnsgn">
                  Patient Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2438" data-mp="3qdfc6pb127y6">
                  Age/Sex
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2439" data-mp="1espmngtoqfba">
                  Registered
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2440" data-mp="15x9qgt5gjo2j">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-2441" data-mp="w16ktdbed7au">
              {filteredPatients.length > 0 ? filteredPatients.map(patient => <tr key={patient.id} className="hover:bg-gray-50" data-id="element-2442" data-mp="mz5ocinzmpa8">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600" data-id="element-2443" data-mp="n1jlqy654dky">
                      {patient.bmcFileNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-2444" data-mp="2hc59erb50bow">
                      {patient.firstName} {patient.lastName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2445" data-mp="2vhxis1am2y66">
                      {patient.age} / {patient.sex}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2446" data-mp="2miaen7qhze9f">
                      {new Date(patient.registeredAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2447" data-mp="2rf0ze8d8yy16">
                      <Button variant="outline" size="sm" onClick={() => setSelectedPatient(patient)} data-id="element-2448" data-mp="3f551ucep8rql">
                        <FileText className="w-4 h-4 mr-2" data-id="element-2449" data-mp="35b5y58yz8zwj" /> View File
                      </Button>
                    </td>
                  </tr>) : <tr data-id="element-2450" data-mp="1tvpr6id57mb5">
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500" data-id="element-2451" data-mp="1yspysxpn4x4d">
                    No patients found matching your search.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
};